import * as React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import axios from 'axios'
import {
  Container,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  TextField,
} from '@material-ui/core'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { showNotification } from '../../store/Notification/actionCreator'
import { setBreadcrumbs } from '../../store/Layout/actionCreator'
import { withAuth } from '@praxis/component-auth'
import apiConfig from '../../apiConfig'
import { deleteImage, indexToElastic } from '../../store/Admin/actionCreator'
import { ADMIN_ACTIONS } from '../../store/Admin/constants'

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    overflow: 'hidden',
  },
  container: {
    padding: theme.spacing(3),
    maxWidth: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  adminTitle: {
    marginBottom: '25px',
  },
  formControl: {
    width: 500,
  },
  executeButton: {
    width: 500,
  },
}))

const { getAdminDeleteApi, getAdminIndexApi } = apiConfig

// Define the handlers for each action
const actionHandlers = {
  deleteImage: deleteImage,
  indexToElastic: indexToElastic,
}

const Admin = (props) => {
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const classes = useStyles()

  const validVersionRegex = /^v\d+$/
  const fileNameSepRegex = /[^\d\w_.-]+/

  React.useEffect(() => {
    dispatch(setBreadcrumbs(props.customPath, pathname))
  }, [props])

  const [selectedAction, setSelectedAction] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [result, setResult] = React.useState(null)
  const [formValues, setFormValues] = React.useState({})
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false)
  const [errors, setErrors] = React.useState({})

  const handleActionChange = (event) => {
    setFormValues({})
    setErrors({})
    setHasAttemptedSubmit(false)
    setSelectedAction(event.target.value)
    setResult(null)
  }

  const handleDeleteResponse = (response = {}) => {
    const {
      metadataDeleted,
      workflowInfoDeleted,
      searchDocsDeleted,
      historyDeleted,
    } = response.data

    const statusMap = {
      metadataDeleted: 'metadata',
      workflowInfoDeleted: 'workflow info',
      searchDocsDeleted: 'search docs',
      historyDeleted: 'history',
    }

    const successful = Object.keys(statusMap)
      .filter((key) => response?.data?.[key])
      .map((key) => statusMap[key])

    if (successful.length === 4) {
      dispatch(
        showNotification(
          true,
          'Successfully deleted metadata, workflow info, search docs and history',
          'success',
          2000
        )
      )
    } else if (successful.length === 0) {
      dispatch(showNotification(true, 'Deletion unsuccessful', 'error', 2000))
    } else {
      const readable = successful.join(', ').replace(/, ([^,]*)$/, ' and $1')
      dispatch(
        showNotification(
          true,
          `Successfully deleted ${readable}`,
          'success',
          2000
        )
      )
    }
  }

  const handleESResponse = (response = {}) => {
    if (response.status === 200) {
      dispatch(showNotification(true, 'Indexing Successful', 'success', 2000))
    } else {
      dispatch(showNotification(true, `Indexing Failed`, 'error', 2000))
    }
  }

  const handleExecute = async () => {
    const selected = ADMIN_ACTIONS.find((o) => o.label === selectedAction)
    if (!selected) return

    const actionKey = selected.value
    const handler = actionHandlers[actionKey]
    if (!handler) return

    setLoading(true)

    try {
      const response = await dispatch(handler(formValues, props.accessToken))

      // Use the result if you want to manually inspect it
      setResult(response)

      // Show toast or response summary based on action
      switch (actionKey) {
        case 'deleteImage':
          handleDeleteResponse(response)
          break

        case 'indexToElastic':
          handleESResponse(response)
          break

        default:
          break
      }
    } catch (err) {
      setResult({ error: err.message })
      dispatch(showNotification(true, 'Operation failed', 'error', 2000))
    }

    setLoading(false)
  }

  const handleExecuteClick = () => {
    setHasAttemptedSubmit(true)

    const newErrors = {}
    if (!formValues?.fileNames || formValues.fileNames.length === 0) {
      newErrors['fileNames'] = 'At least one image name is required'
    }

    if (
      selectedAction === 'Delete Image' &&
      (!formValues?.version || !validVersionRegex.test(formValues.version))
    ) {
      newErrors['version'] =
        'Version must be in the format v<number> (e.g., v1)'
    }

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      handleExecute()
    }
  }

  const handleOnChangeforTextfield = (e) => {
    const raw = e.target.value
    const cleanList = processFileNames(raw)

    setFormValues((prev) => ({
      ...prev,
      fileNamesRaw: raw,
      fileNames: cleanList,
    }))

    if (errors?.fileNames && cleanList.length > 0) {
      setErrors((prev) => ({ ...prev, fileNames: '' }))
    }
  }

  const processFileNames = (raw) =>
    raw
      .split(fileNameSepRegex)
      .map((name) => name.trim())
      .filter((name) => name.length > 0)
      .map((name) =>
        name.toLowerCase().endsWith('.tif') ? name : `${name}.tif`
      )

  const renderFormForAction = (action) => {
    switch (action) {
      case 'Delete Image':
        return (
          <Grid container spacing={2} style={{ maxWidth: 400 }}>
            <Grid item xs={12}>
              <TextField
                label="Enter file names"
                variant="outlined"
                size="small"
                fullWidth
                value={formValues.fileNamesRaw || ''}
                error={!!errors.fileNames}
                helperText={
                  errors.fileNames ||
                  'Separate file names with commas or spaces'
                }
                onChange={(e) => {
                  handleOnChangeforTextfield(e)
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Version"
                variant="outlined"
                size="small"
                fullWidth
                value={formValues.version || ''}
                error={
                  hasAttemptedSubmit &&
                  !validVersionRegex.test(formValues.version)
                }
                helperText={
                  hasAttemptedSubmit &&
                  !validVersionRegex.test(formValues.version)
                    ? 'Invalid Version'
                    : ''
                }
                onChange={(e) =>
                  setFormValues({ ...formValues, version: e.target.value })
                }
              />
            </Grid>
          </Grid>
        )
      case 'Index to ElasticSearch':
        return (
          <Grid container spacing={2} style={{ maxWidth: 400 }}>
            <Grid item xs={12}>
              <TextField
                label="Enter file names"
                variant="outlined"
                size="small"
                fullWidth
                value={formValues.fileNamesRaw || ''}
                error={!!errors.fileNames}
                helperText={
                  errors.fileNames ||
                  'Separate file names with commas or spaces'
                }
                onChange={(e) => {
                  handleOnChangeforTextfield(e)
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Index Name"
                variant="outlined"
                size="small"
                fullWidth
                value={formValues.indexName || ''}
                onChange={(e) =>
                  setFormValues({ ...formValues, indexName: e.target.value })
                }
              />
            </Grid>
          </Grid>
        )
      default:
        return null
    }
  }

  return (
    <div className={classes.content}>
      <Container className={classes.container}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <Typography variant="h5" className={classes.adminTitle}>
                Admin Tools
              </Typography>
              <FormControl className={classes.formControl} variant="outlined">
                <InputLabel>Select an Action</InputLabel>
                <Select
                  value={selectedAction}
                  onChange={handleActionChange}
                  label="Select an Action"
                >
                  {ADMIN_ACTIONS.map((opt) => (
                    <MenuItem key={opt.label} value={opt.label}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {selectedAction && (
                  <div style={{ marginTop: 16 }}>
                    {renderFormForAction(selectedAction)}

                    <Button
                      className={classes.executeButton}
                      variant="contained"
                      color="primary"
                      disabled={
                        loading ||
                        !formValues.fileNames ||
                        formValues.fileNames.length === 0 ||
                        (selectedAction === 'Delete Image' &&
                          !formValues.version)
                      }
                      onClick={handleExecuteClick}
                      style={{ marginTop: 16 }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Execute'}
                    </Button>
                  </div>
                )}
              </FormControl>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

const mapAuthToProps = (auth) => {
  return {
    company: auth.session?.userInfo?.company,
    accessToken: auth.session?.userInfo?.accessToken,
    memberOf: auth.session?.userInfo?.memberOf,
    lanId: auth.session?.userInfo?.lanId,
  }
}

export default withAuth(mapAuthToProps)(Admin)
